import { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import TelemetriaService from "../../services/resource/telemetriaService";
import { useAuth } from "../../context/authContext";
import FilialService from "../../services/resource/filialService";
import FuncionarioService from "../../services/resource/funcionarioService";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import DashboardAvisoMotoristaConcept from "./DashboardAvisoMotoristaConcept";
import { captureElementToImage } from "../../utils/captureElementToImage";

// ─── Constantes ───────────────────────────────────────────────────────────────
const META = 98;
const MESES = [
    { label: "Janeiro", value: 1 }, { label: "Fevereiro", value: 2 },
    { label: "Março", value: 3 }, { label: "Abril", value: 4 },
    { label: "Maio", value: 5 }, { label: "Junho", value: 6 },
    { label: "Julho", value: 7 }, { label: "Agosto", value: 8 },
    { label: "Setembro", value: 9 }, { label: "Outubro", value: 10 },
    { label: "Novembro", value: 11 }, { label: "Dezembro", value: 12 },
];

const ANOS = [{ label: "2026", value: 2026 }, { label: "2027", value: 2027 }];

// ─── Paleta light ─────────────────────────────────────────────────────────────
const C = {
    bg: "#f1f5f9",
    surface: "#ffffff",
    surfaceAlt: "#f8fafc",
    border: "#e2e8f0",
    borderStrong: "#cbd5e1",
    accent: "#2563eb",
    accentSoft: "#eff6ff",
    accentMid: "#bfdbfe",
    green: "#16a34a",
    greenSoft: "#f0fdf4",
    greenMid: "#bbf7d0",
    red: "#dc2626",
    redSoft: "#fef2f2",
    redMid: "#fecaca",
    yellow: "#d97706",
    yellowSoft: "#fffbeb",
    text: "#0f172a",
    textSub: "#334155",
    muted: "#64748b",
    grid: "#e2e8f0",
};

// ─── Estado vazio (sem dados) ─────────────────────────────────────────────────
const EMPTY = {
    totalCondutores: 0,
    condutoresAcimaMeta: 0,
    condutoresAbaixoMeta: 0,
    mediaGeralSegurancaViaria: 0,
    mediaSegurancaViaria: 0,
    mediaManutencao: 0,
    mediaPerformance: 0,
    evolucaoDiaria: [],
    rankingFiliais: [],
    tabelaCondutores: [],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => (v == null ? "—" : parseFloat(v).toFixed(1));
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

// ═════════════════════════════════════════════════════════════════════════════
// Componentes UI
// ═════════════════════════════════════════════════════════════════════════════

function KpiCard({ label, value, sub, color, bgColor, icon }) {
    return (
        <div
            className="rounded-2xl p-4 flex flex-col gap-2 shadow-card"
            style={{ background: C.surface, border: `1px solid ${C.border}` }}
        >
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: C.muted }}>
                    {label}
                </span>
                <span
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-base"
                    style={{ background: bgColor, color }}
                >
                    {icon}
                </span>
            </div>
            <span className="text-3xl font-black" style={{ color: C.text }}>
                {value}
            </span>
            {sub && (
                <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full w-fit"
                    style={{ background: bgColor, color }}
                >
                    {sub}
                </span>
            )}
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <div className="flex items-center gap-3 mb-2">
            <span className="text-sm font-bold tracking-wider uppercase" style={{ color: C.textSub }}>
                {children}
            </span>
            <div className="h-px flex-1" style={{ background: C.border }} />
        </div>
    );
}

function Badge({ ok }) {
    return (
        <span
            className="px-2.5 py-0.5 rounded-full text-xs font-bold"
            style={{
                background: ok ? C.greenSoft : C.redSoft,
                color: ok ? C.green : C.red,
                border: `1px solid ${ok ? C.greenMid : C.redMid}`,
            }}
        >
            {ok ? "✓ Atingiu" : "✗ Abaixo"}
        </span>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="text-5xl opacity-30">📊</span>
            <p className="text-sm font-medium" style={{ color: C.muted }}>
                Selecione o mês, ano e aplique os filtros para visualizar os dados.
            </p>
        </div>
    );
}

function LoadingOverlay() {
    return (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div
                className="w-10 h-10 rounded-full border-4 animate-spin"
                style={{ borderColor: C.accentMid, borderTopColor: C.accent }}
            />
            <p className="text-sm font-medium" style={{ color: C.muted }}>
                Carregando dados…
            </p>
        </div>
    );
}

// ── Evolução Diária ───────────────────────────────────────────────────────────
function ChartEvolucao({ data }) {
    if (!data?.length) return <EmptyState />;

    const opts = {
        chart: { type: "line", background: "transparent", toolbar: { show: false }, animations: { enabled: true, speed: 400 } },
        stroke: { curve: "smooth", width: [3, 2, 2] },
        colors: [C.accent, C.green, C.yellow],
        xaxis: {
            categories: data.map((d) => d.data),
            labels: { style: { colors: C.muted, fontSize: "11px" }, rotate: -30 },
            axisBorder: { color: C.border },
            axisTicks: { color: C.border },
        },
        yaxis: { min: 70, max: 100, labels: { style: { colors: C.muted } } },
        grid: { borderColor: C.grid, strokeDashArray: 4 },
        legend: { labels: { colors: C.textSub }, position: "top" },
        tooltip: { theme: "light" },
        annotations: {
            yaxis: [{
                y: META,
                borderColor: C.red,
                borderWidth: 2,
                strokeDashArray: 6,
                label: { text: `Meta ${META}%`, style: { color: "#fff", background: C.red, fontWeight: 700 } },
            }],
        },
    };

    const series = [
        { name: "Seg. Viária", data: data.map((d) => parseFloat(fmt(d.mediaSegurancaViaria))) },
        { name: "Manutenção", data: data.map((d) => parseFloat(fmt(d.mediaManutencao))) },
        { name: "Performance", data: data.map((d) => parseFloat(fmt(d.mediaPerformance))) },
    ];

    return <ReactApexChart options={opts} series={series} type="line" height={280} />;
}

// ── Donut ─────────────────────────────────────────────────────────────────────
function ChartDonut({ acima, abaixo }) {
    if (acima + abaixo === 0) return <EmptyState />;

    const opts = {
        chart: { background: "transparent" },
        labels: ["Atingiram Meta", "Abaixo da Meta"],
        colors: [C.green, C.red],
        legend: { labels: { colors: C.textSub }, position: "bottom" },
        plotOptions: {
            pie: {
                donut: {
                    size: "70%",
                    labels: { show: true, total: { show: true, label: "Total", color: C.textSub, fontWeight: 700 } },
                },
            },
        },
        dataLabels: { enabled: false },
        tooltip: { theme: "light" },
        stroke: { colors: [C.surface] },
    };

    return <ReactApexChart options={opts} series={[acima, abaixo]} type="donut" height={260} />;
}

// ── Radar Categorias ──────────────────────────────────────────────────────────
function ChartRadar({ seg, manu, perf }) {
    const opts = {
        chart: { background: "transparent", toolbar: { show: false } },
        xaxis: { categories: ["Seg. Viária", "Manutenção", "Performance"] },
        colors: [C.accent],
        fill: { opacity: 0.2 },
        stroke: { width: 2 },
        markers: { size: 4, colors: [C.accent] },
        yaxis: { show: false, min: 0, max: 100 },
        plotOptions: {
            radar: { polygons: { strokeColors: C.border, fill: { colors: [C.surfaceAlt, C.surface] } } },
        },
        tooltip: { theme: "light" },
    };

    return (
        <ReactApexChart
            options={opts}
            series={[{ name: "Média", data: [parseFloat(fmt(seg)), parseFloat(fmt(manu)), parseFloat(fmt(perf))] }]}
            type="radar"
            height={240}
        />
    );
}

// ── Ranking Filiais ───────────────────────────────────────────────────────────
function ChartFiliais({ filiais }) {
    if (!filiais?.length) return <EmptyState />;

    const sorted = [...filiais].sort((a, b) => b.mediaSegurancaViaria - a.mediaSegurancaViaria);

    const opts = {
        chart: { type: "bar", background: "transparent", toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 6, dataLabels: { position: "top" } } },
        colors: sorted.map((f) => (f.mediaSegurancaViaria >= META ? C.green : C.yellow)),
        xaxis: {
            categories: sorted.map((f) => f.filialNome),
            min: 80, max: 100,
            labels: { style: { colors: C.muted } },
            axisBorder: { color: C.border },
        },
        yaxis: { labels: { style: { colors: C.textSub } } },
        grid: { borderColor: C.grid, strokeDashArray: 4 },
        dataLabels: {
            enabled: true,
            style: { colors: [C.textSub], fontWeight: 700 },
            formatter: (v) => `${fmt(v)}%`,
            offsetX: 8,
        },
        tooltip: { theme: "light" },
        annotations: {
            xaxis: [{
                x: META,
                borderColor: C.red,
                borderWidth: 2,
                strokeDashArray: 6,
                label: { text: `Meta ${META}%`, style: { color: "#fff", background: C.red, fontWeight: 700 } },
            }],
        },
    };

    return (
        <ReactApexChart
            options={opts}
            series={[{ name: "Média Seg. Viária", data: sorted.map((f) => parseFloat(fmt(f.mediaSegurancaViaria))) }]}
            type="bar"
            height={Math.max(200, sorted.length * 52)}
        />
    );
}

// ── Modal: evolução diária de um condutor ─────────────────────────────────────
function ModalEvolucaoCondutor({ condutor, ano, mes, onClose }) {
    const telemetriaService = useMemo(() => new TelemetriaService(), []);
    const [dias, setDias] = useState(null);

    useEffect(() => {
        setDias(null);
        telemetriaService
            .listar({ ano, mes, funcionarioId: condutor.funcionarioId })
            .then((res) => setDias([...res.data].sort((a, b) => a.data.localeCompare(b.data))))
            .catch(() => setDias([]));
    }, [condutor.funcionarioId, ano, mes]);

    const fmtData = (d) => d.split("-").reverse().slice(0, 2).join("/");

    const opts = {
        chart: { type: "line", background: "transparent", toolbar: { show: false } },
        stroke: { curve: "smooth", width: [3, 2, 2] },
        colors: [C.accent, C.green, C.yellow],
        markers: { size: 3 },
        xaxis: {
            categories: (dias || []).map((d) => fmtData(d.data)),
            labels: { style: { colors: C.muted, fontSize: "11px" }, rotate: -45 },
        },
        yaxis: { min: 0, max: 100, labels: { style: { colors: C.muted } } },
        grid: { borderColor: C.grid, strokeDashArray: 4 },
        legend: { labels: { colors: C.textSub }, position: "top" },
        tooltip: { theme: "light", y: { formatter: (v) => `${fmt(v)}%` } },
        annotations: {
            yaxis: [{
                y: META,
                borderColor: C.red,
                strokeDashArray: 6,
                label: { text: `Meta ${META}%`, style: { color: "#fff", background: C.red, fontWeight: 700 } },
            }],
        },
    };

    const series = [
        { name: "Seg. Viária", data: (dias || []).map((d) => d.segurancaViaria) },
        { name: "Manutenção", data: (dias || []).map((d) => d.manutencao) },
        { name: "Performance", data: (dias || []).map((d) => d.performance) },
    ];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,42,0.5)" }}
            onClick={onClose}
        >
            <div
                className="w-full max-w-4xl rounded-2xl p-5 shadow-card"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <h2 className="text-lg font-black" style={{ color: C.text }}>{condutor.nomeFuncionario}</h2>
                        <p className="text-xs" style={{ color: C.muted }}>
                            {condutor.filialNome} · Desempenho diário em {mes}/{ano}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-xl leading-none px-2" style={{ color: C.muted }} aria-label="Fechar">×</button>
                </div>
                {dias === null ? <LoadingOverlay /> : !dias.length ? <EmptyState /> : (
                    <ReactApexChart options={opts} series={series} type="line" height={340} />
                )}
            </div>
        </div>
    );
}

// ── Tabela de Condutores ──────────────────────────────────────────────────────
function TabelaCondutores({ rows, search, onSelect }) {
    if (!rows?.length) return <EmptyState />;

    const filtered = rows.filter((r) =>
        [r.nomeFuncionario, r.codigoFuncionario, r.filialNome].some((v) =>
            v?.toLowerCase().includes(search.toLowerCase())
        )
    );

    return (
        <div className="overflow-x-auto rounded-xl overflow-auto" style={{ border: `1px solid ${C.border}` }}>
            <table className="w-full text-sm">
                <thead>
                    <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                        {["Filial", "Código", "Funcionário", "Performance", "Manutenção", "Segurança Viária", "Status"].map((h) => (
                            <th
                                key={h}
                                className="px-2 py-3 text-left font-semibold tracking-wider text-xs uppercase"
                                style={{ color: C.muted }}
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((r, i) => (
                        <tr
                            key={i}
                            className="transition-colors duration-150 hover:bg-table-row-hover text-xs cursor-pointer"
                            style={{ borderBottom: `1px solid ${C.border}` }}
                            title="Clique para ver o desempenho diário"
                            onClick={() => onSelect?.(r)}
                        >
                            <td className="px-2 py-3 font-medium" style={{ color: C.textSub }}>{r.filialNome}</td>
                            <td className="px-2 py-3 font-mono text-xs" style={{ color: C.muted }}>{r.codigoFuncionario}</td>
                            <td className="px-2 py-3 font-medium" style={{ color: C.text }}>{r.nomeFuncionario} <span className="text-xs text-muted-foreground px-2">{r.cargo}</span></td>

                            <td className="px-2 py-3">
                                <div className="flex items-center gap-2">

                                    <span className="font-bold w-12 text-right text-foreground">
                                        {fmt(r.mediaPerformance)}%
                                    </span>
                                </div>
                            </td>
                            <td className="px-2 py-3">
                                <div className="flex items-center gap-2">

                                    <span className="font-bold w-12 text-right text-foreground" >
                                        {fmt(r.mediaManutencao)}%
                                    </span>
                                </div>
                            </td>
                            <td className="px-2 py-3">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 h-1.5 rounded-full" style={{ background: C.border }}>
                                        <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{
                                                width: `${Math.min(100, r.mediaSeguracaViaria)}%`,
                                                background: r.atingiuMeta ? C.green : C.red,
                                            }}
                                        />
                                    </div>
                                    <span className="font-bold w-12 text-right" style={{ color: r.atingiuMeta ? C.green : C.red }}>
                                        {fmt(r.mediaSeguracaViaria)}%
                                    </span>
                                </div>
                            </td>
                            <td className="px-2 py-3"><Badge ok={r.atingiuMeta} /></td>
                        </tr>
                    ))}
                    {filtered.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-4 py-8 text-center" style={{ color: C.muted }}>
                                Nenhum registro encontrado para "{search}"
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ── Condutores sem vínculo (somente ADMIN) ────────────────────────────────────
function CondutoresNaoVinculados({ ano, mes, filialId, onVinculado }) {
    const telemetriaService = useMemo(() => new TelemetriaService(), []);
    const funcionarioService = useMemo(() => new FuncionarioService(), []);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [funcionariosPorFilial, setFuncionariosPorFilial] = useState({});
    const [selecionados, setSelecionados] = useState({});
    const [salvando, setSalvando] = useState(null);

    const carregar = () => {
        setLoading(true);
        telemetriaService
            .listarNaoVinculados({ ano, mes, filialId })
            .then((res) => setRows(res.data))
            .catch((e) => console.error(e))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        carregar();
    }, [ano, mes, filialId]);

    const carregarFuncionarios = (id) => {
        if (funcionariosPorFilial[id]) return;
        funcionarioService.listar({ filialId: id }).then((res) => {
            setFuncionariosPorFilial((prev) => ({ ...prev, [id]: res.data }));
        });
    };

    useEffect(() => {
        [...new Set(rows.map((r) => r.filialId))].forEach(carregarFuncionarios);
    }, [rows]);

    const vincular = (r) => {
        const funcionarioId = selecionados[r.motoristaId];
        if (!funcionarioId) return;

        setSalvando(r.motoristaId);
        telemetriaService
            .vincularCondutor(r.motoristaId, funcionarioId)
            .then(() => {
                toast.success(`${r.motoristaNome} vinculado com sucesso`);
                carregar();
                onVinculado();
            })
            .catch((e) => toast.error(e?.response?.data?.message || "Não foi possível vincular o condutor"))
            .finally(() => setSalvando(null));
    };

    return (
        <div className="rounded-2xl p-5 mt-2 shadow-card" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
            <SectionTitle>Condutores sem vínculo com funcionário</SectionTitle>
            <p className="text-xs mb-3" style={{ color: C.muted }}>
                Condutores do Trixlog com medições no período que ainda não estão vinculados a um funcionário —
                por isso não entram nos indicadores acima. Ao vincular, todas as medições sem vínculo do condutor são atribuídas ao funcionário.
            </p>

            {loading ? (
                <p className="text-sm" style={{ color: C.muted }}>Carregando…</p>
            ) : !rows.length ? (
                <p className="text-sm font-medium" style={{ color: C.green }}>
                    <i className="fa fa-check-circle mr-1" /> Todos os condutores do período estão vinculados.
                </p>
            ) : (
                <div className="overflow-x-auto rounded-xl" style={{ border: `1px solid ${C.border}` }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ background: C.surfaceAlt, borderBottom: `1px solid ${C.border}` }}>
                                {["Filial", "Condutor (Trixlog)", "Dias", "Seg. Viária", "Funcionário", ""].map((h) => (
                                    <th key={h} className="px-2 py-3 text-left font-semibold tracking-wider text-xs uppercase" style={{ color: C.muted }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.motoristaId} className="text-xs" style={{ borderBottom: `1px solid ${C.border}` }}>
                                    <td className="px-2 py-3 font-medium" style={{ color: C.textSub }}>{r.filialNome}</td>
                                    <td className="px-2 py-3 font-medium" style={{ color: C.text }}>
                                        {r.motoristaNome}
                                        <span className="font-mono px-2" style={{ color: C.muted }}>#{r.motoristaId}</span>
                                    </td>
                                    <td className="px-2 py-3" style={{ color: C.muted }}>{r.diasMedidos}</td>
                                    <td className="px-2 py-3 font-bold" style={{ color: r.mediaSegurancaViaria >= META ? C.green : C.red }}>
                                        {fmt(r.mediaSegurancaViaria)}%
                                    </td>
                                    <td className="px-2 py-3">
                                        <select
                                            value={selecionados[r.motoristaId] ?? ""}
                                            onChange={(e) => setSelecionados((prev) => ({ ...prev, [r.motoristaId]: e.target.value }))}
                                            className="w-64 rounded-lg px-2 py-1.5 text-xs focus:outline-none"
                                            style={{ background: C.surfaceAlt, border: `1px solid ${C.borderStrong}`, color: C.text }}
                                        >
                                            <option value="">Selecione o funcionário…</option>
                                            {(funcionariosPorFilial[r.filialId] || []).map((f) => (
                                                <option key={f.id} value={f.id}>{f.nome}{f.cargo ? ` — ${f.cargo}` : ""}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-2 py-3">
                                        <Button
                                            label="Vincular"
                                            onClick={() => vincular(r)}
                                            disabled={!selecionados[r.motoristaId] || salvando === r.motoristaId}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
// App principal
// ═════════════════════════════════════════════════════════════════════════════
export default function DashboardSegurancaViaria() {
    const { user } = useAuth();
    const isRoleComissao = user.perfis.includes("COMISSOES")
    const isAdmin = user.perfis.includes("ADMIN")

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        if (mesInicial === 12) { mesInicial = 1; anoInicial += 1; }
        else { mesInicial += 1; }
    }

    const [dashData, setDashData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [condutorSelecionado, setCondutorSelecionado] = useState(null);
    const [filiais, setFiliais] = useState([]);
    const [filialId, setFilialId] = useState(isRoleComissao ? user.filialId : null);
    const [ano, setAno] = useState(anoInicial);
    const [mes, setMes] = useState(mesInicial);

    const telemetriaService = new TelemetriaService();
    const filialService = useMemo(() => new FilialService(), []);

    const carregar = () => {
        setLoading(true);
        const data = {
            ano: ano,
            mes: mes,
            filialId: isRoleComissao ? user.filialId : filialId,
        };

        telemetriaService
            .dashboard(data)
            .then((res) => {
                setDashData(res.data);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        carregar();
    }, [filialId, ano, mes]);

    // Carrega ao montar com mês/ano atual
    useEffect(() => {
        buscarFiliais();
    }, []);

    const atualizaRanking = () => {
        setLoading(true);

        const data = {
            ano: ano,
            mes: mes,
        };
        telemetriaService.sincronizar(data).then(() => {
            toast.success("Todos os dados foram atualizadas entre o período selecionado");
            carregar();
        }).catch((e) => console.error(e)).finally(() => setLoading(false));
    }

    const buscarFiliais = () => {
        filialService.listar({ tipoDistribuidor: 'LOGISTICO' }).then(res => {
            setFiliais(res.data);
        });
    }

    const d = dashData || EMPTY;
    const periodoLabel = `${mes}/${ano}`;

    return (
        <div className="min-h-screen p-4 md:p-6 lg:p-8 font-sans" style={{ background: C.bg, color: C.text }}>

            {/* ── Header ── */}
            <div className="mb-3 flex items-start justify-between flex-wrap gap-2">
                <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shadow-card"
                            style={{ background: C.accent, color: "#fff" }}
                        >
                            <i className="fa fa-truck" />
                        </div>
                        <h1 className="text-2xl md:text-3xl font-black tracking-tight" style={{ color: C.text }}>
                            Segurança Viária
                        </h1>
                        <span
                            className="px-2.5 py-0.5 rounded-full text-xs font-bold"
                            style={{ background: C.accentSoft, color: C.accent, border: `1px solid ${C.accentMid}` }}
                        >
                            Dashboard
                        </span>
                    </div>
                    <p className="text-sm" style={{ color: C.muted }}>
                        Monitoramento de condutores · Meta:{" "}
                        <strong style={{ color: C.accent }}>{META}%</strong> em segurança viária
                    </p>
                </div>


                <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 shadow-card">

                    <div>
                        <i className="fa fa-image text-primary mx-2 cursor-pointer" onClick={() => captureElementToImage("dashboard-segurancia-viaria", 'Segurancia Viaria ' + filialId + mes + ano)} />
                    </div>

                    {!isRoleComissao &&
                        <div>
                            <Button
                                label="Sincronizar"
                                className="mr-3"
                                onClick={atualizaRanking}
                                disabled={loading}
                            />
                        </div>
                    }

                    {!isRoleComissao &&
                        <div>
                            <select
                                value={filialId ?? ""}
                                onChange={e => setFilialId(e.target.value)}
                                className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                            >
                                <option value="">
                                    Todas Filiais
                                </option>
                                {filiais.map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.nome}
                                    </option>
                                ))}
                                <option value={4}>
                                    YORK CAXIAS
                                </option>
                            </select>
                        </div>
                    }

                    <i className="fa fa-calendar-alt text-muted-foreground text-sm mr-1" />
                    <select
                        value={mes}
                        onChange={e => setMes(e.target.value)}
                        className="w-32 border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                    >
                        {MESES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                    <span className="text-muted-foreground">|</span>
                    <select
                        value={ano}
                        onChange={e => setAno(Number(e.target.value))}
                        className="border-0 bg-transparent text-sm font-medium text-foreground focus:outline-none cursor-pointer"
                    >
                        {ANOS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}

                    </select>
                </div>

            </div>
            <div id="dashboard-segurancia-viaria">
                {filialId == 4 ?
                    <DashboardAvisoMotoristaConcept ano={ano} mes={mes} />
                    :
                    <>

                        {/* ── Conteúdo ── */}
                        {loading ? (
                            <LoadingOverlay />
                        ) : !dashData ? (
                            <EmptyState />
                        ) : (
                            <>
                                {/* KPIs */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                                    <KpiCard
                                        label="Total Condutores"
                                        value={d.totalCondutores}
                                        sub={`em ${periodoLabel}`}
                                        color={C.accent} bgColor={C.accentSoft} icon={<i className="fa fa-users" />}
                                    />
                                    <KpiCard
                                        label="Acima da Meta"
                                        value={d.condutoresAcimaMeta}
                                        sub={`${pct(d.condutoresAcimaMeta, d.totalCondutores)}% do total`}
                                        color={C.green} bgColor={C.greenSoft} icon={<i className="fa fa-check-square" />}
                                    />
                                    <KpiCard
                                        label="Abaixo da Meta"
                                        value={d.condutoresAbaixoMeta}
                                        sub={`${pct(d.condutoresAbaixoMeta, d.totalCondutores)}% do total`}
                                        color={C.red} bgColor={C.redSoft} icon={<i className="fa fa-exclamation-triangle" />}
                                    />
                                    <KpiCard
                                        label="Média Seg. Viária"
                                        value={`${fmt(d.mediaGeralSegurancaViaria)}%`}
                                        sub={d.mediaGeralSegurancaViaria >= META ? "✓ Acima da meta" : "✗ Abaixo da meta"}
                                        color={d.mediaGeralSegurancaViaria >= META ? C.green : C.red}
                                        bgColor={d.mediaGeralSegurancaViaria >= META ? C.greenSoft : C.redSoft}
                                        icon={<i className="fa fa-chart-line" />}
                                    />
                                </div>

                                {/* Evolução Diária */}
                                <div className="rounded-2xl p-5 mb-2 shadow-card" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                                    <SectionTitle>Desempenho Diário</SectionTitle>
                                    <ChartEvolucao data={d.evolucaoDiaria} />
                                </div>

                                {/* Donut + Radar */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                    <div className="rounded-2xl p-5 shadow-card" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                                        <SectionTitle>Atingimento da Meta</SectionTitle>
                                        <ChartDonut acima={d.condutoresAcimaMeta} abaixo={d.condutoresAbaixoMeta} />
                                    </div>

                                    <div className="rounded-2xl p-5 shadow-card" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                                        <SectionTitle>Média por Categoria no Período</SectionTitle>
                                        <ChartRadar seg={d.mediaSegurancaViaria} manu={d.mediaManutencao} perf={d.mediaPerformance} />
                                        <div className="flex justify-around mt-2">
                                            {[
                                                { label: "Seg. Viária", val: d.mediaSegurancaViaria, color: C.accent, bg: C.accentSoft },
                                                { label: "Manutenção", val: d.mediaManutencao, color: C.green, bg: C.greenSoft },
                                                { label: "Performance", val: d.mediaPerformance, color: C.yellow, bg: C.yellowSoft },
                                            ].map(({ label, val, color, bg }) => (
                                                <div key={label} className="text-center px-3 py-2 rounded-xl" style={{ background: bg }}>
                                                    <div className="text-xl font-black" style={{ color }}>{fmt(val)}%</div>
                                                    <div className="text-xs font-medium" style={{ color: C.muted }}>{label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Ranking por Filial */}
                                <div className="rounded-2xl p-5 mb-2 shadow-card" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                                    <SectionTitle>Ranking por Filial</SectionTitle>
                                    <ChartFiliais filiais={d.rankingFiliais} />

                                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {[...d.rankingFiliais]
                                            .sort((a, b) => b.mediaSegurancaViaria - a.mediaSegurancaViaria)
                                            .map((f, i) => (
                                                <div
                                                    key={f.filialNome}
                                                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                                                    style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
                                                >
                                                    <span
                                                        className="text-base font-black w-7 text-center"
                                                        style={{ color: i === 0 ? "#d97706" : i === 1 ? "#6b7280" : i === 2 ? "#b45309" : C.muted }}
                                                    >
                                                        {i + 1}º
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-semibold text-sm truncate" style={{ color: C.text }}>{f.filialNome}</div>
                                                        <div className="text-xs" style={{ color: C.muted }}>{f.condutoresAcimaMeta}/{f.totalCondutores} na meta</div>
                                                    </div>
                                                    <span
                                                        className="font-black text-sm px-2 py-0.5 rounded-lg"
                                                        style={{
                                                            color: f.mediaSegurancaViaria >= META ? C.green : C.yellow,
                                                            background: f.mediaSegurancaViaria >= META ? C.greenSoft : C.yellowSoft,
                                                        }}
                                                    >
                                                        {fmt(f.mediaSegurancaViaria)}%
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                {/* Tabela de Condutores */}
                                <div className="rounded-2xl p-5 shadow-card" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                                    <SectionTitle>Condutores — Detalhamento Individual</SectionTitle>
                                    <div className="mb-4 flex items-center gap-2 flex-wrap">
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: C.muted }}><i className="fa fa-search pr-1" /></span>
                                            <input
                                                type="text"
                                                placeholder="Buscar por nome, código ou filial…"
                                                className="pl-8 pr-4 py-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-150"
                                                style={{ background: C.surfaceAlt, border: `1px solid ${C.borderStrong}`, color: C.text, width: 300 }}
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                        </div>
                                        <span className="text-xs" style={{ color: C.muted }}>
                                            {(d.tabelaCondutores || []).filter((r) =>
                                                [r.nomeFuncionario, r.codigoFuncionario, r.filialNome].some((v) =>
                                                    v?.toLowerCase().includes(search.toLowerCase())
                                                )
                                            ).length}{" "}
                                            de {(d.tabelaCondutores || []).length} registros
                                        </span>
                                    </div>
                                    <TabelaCondutores rows={d.tabelaCondutores} search={search} onSelect={setCondutorSelecionado} />
                                </div>

                                {condutorSelecionado && (
                                    <ModalEvolucaoCondutor
                                        condutor={condutorSelecionado}
                                        ano={ano}
                                        mes={mes}
                                        onClose={() => setCondutorSelecionado(null)}
                                    />
                                )}

                                {isAdmin && (
                                    <CondutoresNaoVinculados
                                        ano={ano}
                                        mes={mes}
                                        filialId={filialId}
                                        onVinculado={carregar}
                                    />
                                )}
                            </>
                        )}
                    </>
                }
            </div>
        </div>
    );
}
